// Single source of truth for in-app routes. Both App.tsx (for dispatching
// the URL to the right page) and ChatBubble's client-tool navigate handler
// (for validating agent-requested paths) consult this table.

export type RouteKind = 'tab' | 'recipe-view' | 'recipe-edit' | 'meal-view' | 'usage' | 'docs' | 'grant-accept'

export const ROUTES: ReadonlyArray<{ kind: RouteKind; pattern: RegExp }> = [
  { kind: 'tab',          pattern: /^\/(plan|recipes|pantry|preferences)$/ },
  // Invite-accept landing for account sharing: /grant/<token>. The token is a
  // base64url randToken(24) — letters, digits, '-' and '_'.
  { kind: 'grant-accept', pattern: /^\/grant\/[A-Za-z0-9_-]+$/ },
  { kind: 'recipe-view',  pattern: /^\/recipes\/\d+$/ },
  { kind: 'recipe-edit',  pattern: /^\/recipes\/\d+\/edit$/ },
  // A planned meal opened from a plan slot: the recipe in the context of its
  // date/slot, with status + remove-from-plan controls.
  { kind: 'meal-view',    pattern: /^\/meals\/\d+$/ },
  { kind: 'usage',        pattern: /^\/usage$/ },
  // Extension-less slugs: `/agent-docs`, `/agent-docs/units`, and the nested
  // `/agent-docs/tools/recipe_create`. No `.` in the charclass — the docs are
  // live views, not files, so a slug never carries an extension.
  { kind: 'docs',         pattern: /^\/agent-docs(\/[A-Za-z0-9_-]+){0,2}$/ },
]

export function matchRoute(path: string): RouteKind | null {
  for (const r of ROUTES) if (r.pattern.test(path)) return r.kind
  return null
}

// Per-route query-string contract. A route absent from this map accepts no
// query parameters; a present route validates its own. Keep this in sync with
// the page that reads the param (e.g. PantryTab reads `?tab=`).
const ROUTE_QUERY: Record<string, (params: URLSearchParams) => string | null> = {
  '/pantry': params => {
    for (const key of params.keys()) {
      if (key !== 'tab') return `/pantry only accepts a 'tab' query parameter, not ${JSON.stringify(key)}`
    }
    const tab = params.get('tab')
    if (tab !== null && tab !== 'stock' && tab !== 'shopping')
      return `/pantry tab must be 'stock' or 'shopping', got ${JSON.stringify(tab)}`
    return null
  },
  '/preferences': params => {
    for (const key of params.keys()) {
      if (key !== 'tab') return `/preferences only accepts a 'tab' query parameter, not ${JSON.stringify(key)}`
    }
    const tab = params.get('tab')
    if (tab !== null && tab !== 'personalization' && tab !== 'advanced')
      return `/preferences tab must be 'personalization' or 'advanced', got ${JSON.stringify(tab)}`
    return null
  },
}

// validateNavigatePath enforces relative-path safety AND the route catalog.
// Returns either the (trimmed) path to navigate to, or a human-readable
// error string suitable for surfacing to the agent.
export function validateNavigatePath(raw: unknown): { ok: true; path: string } | { ok: false; error: string } {
  if (typeof raw !== 'string') return { ok: false, error: 'path must be a string' }
  const p = raw.trim()
  if (!p) return { ok: false, error: 'path required' }
  if (!p.startsWith('/') || p.startsWith('//'))
    return { ok: false, error: "path must be a relative in-app route beginning with a single '/'" }
  if (p.includes('://') || p.includes('\\') || p.includes('#'))
    return { ok: false, error: 'path may not contain a scheme, backslash, or fragment' }

  // Split the optional query string off; routing matches on the pathname, the
  // query is validated separately against the per-route contract.
  const qIdx = p.indexOf('?')
  const pathname = qIdx >= 0 ? p.slice(0, qIdx) : p
  const search = qIdx >= 0 ? p.slice(qIdx + 1) : ''

  for (const seg of pathname.split('/')) {
    if (seg === '..' || seg === '.')
      return { ok: false, error: "path may not contain '.' or '..' segments" }
  }
  if (!matchRoute(pathname))
    return { ok: false, error: `path ${JSON.stringify(pathname)} is not a known in-app route` }

  if (search) {
    const validator = ROUTE_QUERY[pathname]
    if (!validator)
      return { ok: false, error: `route ${JSON.stringify(pathname)} does not accept query parameters` }
    const err = validator(new URLSearchParams(search))
    if (err) return { ok: false, error: err }
  }
  return { ok: true, path: p }
}
